import React, { useState, useEffect } from 'react';
import { 
    Ship, Plane, Truck, ShieldCheck, Search, Calculator, FileText, CheckCircle2, 
    Globe, PhoneCall, ArrowRight, ArrowLeft, Package, Clock, Warehouse, Award,
    MessageSquare, AlertCircle, RefreshCw, Send, ChevronRight, Layers, FileCheck
} from 'lucide-react';
import { getTranslation as t, isRTL } from '../utils/translations';
import { useAuth } from '../context/AuthContext';
import { trackShipment } from '../api/client';

const LuxvooShippingHome = () => {
    const isRtl = isRTL();
    const { user } = useAuth();

    // Shipment Tracker State
    const [trackingNumber, setTrackingNumber] = useState('');
    const [trackingResult, setTrackingResult] = useState(null);
    const [trackingLoading, setTrackingLoading] = useState(false);

    // Calculator State
    const [calcData, setCalcData] = useState({
        freightType: 'AIR_EXPRESS',
        origin: 'CN_SHENZHEN',
        destination: 'SA_RIYADH',
        weight: '25',
        cbm: '0.15',
        goodsValue: '1500'
    });
    const [calcResult, setCalcResult] = useState(null);

    // RFQ Sourcing Form State
    const [rfqSubmitted, setRfqSubmitted] = useState(false);
    const [rfqLoading, setRfqLoading] = useState(false);
    const [rfqForm, setRfqForm] = useState({
        productCategory: 'ELECTRONICS',
        productName: '',
        targetQuantity: '500',
        targetBudget: '',
        specifications: '',
        clientName: user?.fullName || '',
        clientPhone: user?.phone || '',
        clientEmail: user?.email || '',
        destinationCountry: 'Saudi Arabia'
    });

    // Handle real tracking lookup from backend database & carrier engine
    const handleTrackShipment = async (e) => {
        e.preventDefault();
        const query = trackingNumber.trim().toUpperCase();
        if (!query) return;

        setTrackingLoading(true);
        try {
            const data = await trackShipment(query);
            if (data && data.found) {
                const steps = (data.timeline || []).map((ev, idx, arr) => ({
                    title: ev.milestoneTitle || ev.status,
                    date: ev.eventTime ? new Date(ev.eventTime).toLocaleString('ar-SA') : 'مسجل بالنظام',
                    completed: true,
                    current: idx === arr.length - 1,
                    location: ev.location || data.destinationCity || 'المملكة',
                    description: ev.description
                }));

                // If only 1 step, append standard pending delivery step
                if (steps.length > 0 && data.status !== 'DELIVERED') {
                    steps.push({
                        title: 'التوصيل النهائي حتى باب المستلم',
                        date: data.estimatedDeliveryDate ? `متوقع: ${new Date(data.estimatedDeliveryDate).toLocaleDateString('ar-SA')}` : 'خلال يومين',
                        completed: false,
                        current: false,
                        location: data.destinationCity || 'عنوان العميل'
                    });
                }

                setTrackingResult({
                    found: true,
                    trackingNo: data.trackingNumber,
                    carrier: data.carrierName || data.carrierCode,
                    carrierCode: data.carrierCode,
                    trackingUrl: data.trackingUrl,
                    origin: data.originCity || 'المستودع الرئيسي',
                    destination: data.destinationCity || 'الرياض',
                    status: data.status,
                    statusText: data.statusText || 'شحنة نشطة',
                    estimatedArrival: data.estimatedDeliveryDate ? new Date(data.estimatedDeliveryDate).toLocaleDateString('ar-SA') : 'خلال 48 ساعة',
                    recipientName: data.recipientName,
                    steps: steps
                });
            } else {
                setTrackingResult({
                    found: false,
                    trackingNo: query,
                    statusText: 'لم يتم العثور على شحنة بهذا الرقم',
                    message: 'يرجى التحقق من صحة رقم التتبع أو التواصل مع خدمة العملاء.'
                });
            }
        } catch (err) {
            console.error("Tracking lookup error:", err);
            setTrackingResult({
                found: false,
                trackingNo: query,
                statusText: 'تعذر الاتصال بخادم التتبع',
                message: 'حدث خطأ أثناء فحص البوليصة، يرجى المحاولة لاحقاً.'
            });
        } finally {
            setTrackingLoading(false);
        }
    };

    // Calculate freight cost
    useEffect(() => {
        const weight = parseFloat(calcData.weight) || 0;
        const cbm = parseFloat(calcData.cbm) || 0;
        const val = parseFloat(calcData.goodsValue) || 0;

        let baseRate = 0;
        let daysEst = '3 - 5 أيام عمل';

        if (calcData.freightType === 'AIR_EXPRESS') {
            baseRate = Math.max(weight * 8.5, 45); // $8.5 per kg
            daysEst = '3 - 5 أيام عمل';
        } else if (calcData.freightType === 'AIR_ECONOMY') {
            baseRate = Math.max(weight * 5.8, 35);
            daysEst = '7 - 10 أيام عمل';
        } else if (calcData.freightType === 'SEA_LCL') {
            baseRate = Math.max(cbm * 180, 120); // $180 per CBM
            daysEst = '18 - 25 يوم عمل';
        } else if (calcData.freightType === 'SEA_FCL_20') {
            baseRate = 2100;
            daysEst = '20 - 28 يوم عمل';
        } else if (calcData.freightType === 'SEA_FCL_40') {
            baseRate = 3400;
            daysEst = '20 - 28 يوم عمل';
        }

        const customsEst = val * 0.05; // 5% customs
        const vatEst = (val + baseRate + customsEst) * 0.15; // 15% VAT
        const total = baseRate + customsEst + vatEst;

        setCalcResult({
            shippingCost: baseRate.toFixed(2),
            customsEst: customsEst.toFixed(2),
            vatEst: vatEst.toFixed(2),
            totalEst: total.toFixed(2),
            deliveryDays: daysEst
        });
    }, [calcData]);

    // Handle RFQ submission
    const handleRfqSubmit = (e) => {
        e.preventDefault();
        if (!rfqForm.productName || !rfqForm.clientPhone) return;

        setRfqLoading(true);
        // Store in localStorage for admin sync
        setTimeout(() => {
            try {
                const existing = JSON.parse(localStorage.getItem('luxvono_sourcing_inquiries') || localStorage.getItem('luxvoo_inquiries') || '[]');
                const newInquiry = {
                    id: `RFQ-${Date.now().toString().slice(-6)}`,
                    name: rfqForm.clientName || 'عميل توريد',
                    email: rfqForm.clientEmail || 'client@luxvono.com',
                    phone: rfqForm.clientPhone,
                    service: `${rfqForm.productName} (${rfqForm.targetQuantity} قطعة) - ${rfqForm.productCategory}`,
                    status: 'طلب جديد (قيد التسعير والتفاوض)',
                    date: new Date().toISOString().split('T')[0],
                    specifications: rfqForm.specifications,
                    destination: rfqForm.destinationCountry
                };
                localStorage.setItem('luxvono_sourcing_inquiries', JSON.stringify([newInquiry, ...existing]));
                localStorage.setItem('luxvoo_inquiries', JSON.stringify([newInquiry, ...existing]));
            } catch (_) {}

            setRfqLoading(false);
            setRfqSubmitted(true);
        }, 800);
    };

    return (
        <div className="luxvoo-shipping-page" dir={isRtl ? 'rtl' : 'ltr'}>
            {/* ── CSS Styles ── */}
            <style>{`
                .luxvoo-shipping-page {
                    background: #060913;
                    color: #f8fafc;
                    font-family: 'Outfit', 'Inter', 'Noto Sans Arabic', sans-serif;
                    min-height: 100vh;
                    overflow-x: hidden;
                }
                .luxvoo-hero {
                    position: relative;
                    padding: 80px 5% 60px 5%;
                    background: radial-gradient(circle at 50% 20%, rgba(212, 175, 55, 0.15) 0%, rgba(6, 9, 19, 0.95) 75%);
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                }
                .luxvoo-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: rgba(212, 175, 55, 0.12);
                    border: 1px solid rgba(212, 175, 55, 0.35);
                    color: #e5c07b;
                    padding: 6px 16px;
                    border-radius: 9999px;
                    font-size: 13px;
                    font-weight: 700;
                    margin-bottom: 20px;
                }
                .luxvoo-card {
                    background: rgba(13, 19, 36, 0.7);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 24px;
                    padding: 30px;
                    backdrop-filter: blur(16px);
                    transition: all 0.3s ease;
                }
                .luxvoo-card:hover {
                    border-color: rgba(212, 175, 55, 0.3);
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
                }
                .luxvoo-input {
                    width: 100%;
                    background: rgba(6, 9, 19, 0.8);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    color: #fff;
                    padding: 12px 18px;
                    border-radius: 14px;
                    font-size: 14px;
                    outline: none;
                    transition: border-color 0.2s;
                }
                .luxvoo-input:focus {
                    border-color: #d4af37;
                }
                .luxvoo-shipping-page {
                    direction: rtl;
                    text-align: right;
                    unicode-bidi: isolate;
                }
                .luxvoo-btn-gold {
                    background: linear-gradient(135deg, #d4af37 0%, #a87e1a 100%);
                    color: #060913 !important;
                    font-weight: 800;
                    padding: 14px 28px;
                    border-radius: 16px;
                    border: none;
                    text-decoration: none !important;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    transition: transform 0.2s, box-shadow 0.2s;
                    box-shadow: 0 4px 20px rgba(212, 175, 55, 0.3);
                }
                .luxvoo-btn-gold:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 25px rgba(212, 175, 55, 0.5);
                    text-decoration: none !important;
                }
                .service-pill {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 18px;
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.06);
                    border-radius: 18px;
                    transition: all 0.2s ease;
                }
                .service-pill:hover {
                    background: rgba(212, 175, 55, 0.08);
                    border-color: rgba(212, 175, 55, 0.3);
                }
            `}</style>

            {/* ── 1. Hero Section ── */}
            <div className="luxvoo-hero">
                <div style={{ maxWidth: '1280px', margin: '0 auto', textAlign: 'center' }}>
                    <div className="luxvoo-badge" style={{ direction: 'rtl', unicodeBidi: 'isolate' }}>
                        <Globe size={16} />
                        <span>منظومة الشحن الدولي والاستيراد المباشر من المصانع • LUXVONO GLOBAL LOGISTICS</span>
                    </div>

                    <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3.4rem)', fontWeight: '900', lineHeight: '1.3', marginBottom: '20px', direction: 'rtl', unicodeBidi: 'isolate' }}>
                        بوابتك الرسمية للاستيراد المباشر من المصانع <br />
                        <span style={{ background: 'linear-gradient(135deg, #d4af37 0%, #00f0ff 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                            الشحن السريع، الفحص الميداني، والتخليص الجمركي
                        </span>
                    </h1>

                    <p style={{ maxWidth: '800px', margin: '0 auto 35px auto', fontSize: '1.1rem', color: '#94a3b8', lineHeight: '1.8', direction: 'rtl', unicodeBidi: 'isolate' }}>
                        نربطك مباشرة بأكثر من 10,000 مصنع معتمد في الصين والعالم (OEM / ODM)، مع ضمان فحص الجودة الميداني بالفيديو، شحن جوي وبحري بأفضل الأسعار، وتخليص جمركي وتوصيل حتى باب مستودعك.
                    </p>

                    {/* Quick Action Buttons */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
                        <a href="#sourcing-rfq" className="luxvoo-btn-gold">
                            <span>طلب استيراد وتسعير فوري (RFQ)</span>
                            <ArrowLeft size={18} />
                        </a>
                        <a href="#tracking-tool" style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#fff',
                            fontWeight: '700',
                            padding: '14px 28px',
                            borderRadius: '16px',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <Search size={18} color="#00f0ff" />
                            <span>تتبع شحنتك المباشرة</span>
                        </a>
                        <a href="#freight-calc" style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#fff',
                            fontWeight: '700',
                            padding: '14px 28px',
                            borderRadius: '16px',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <Calculator size={18} color="#e5c07b" />
                            <span>حاسبة أسعار الشحن والجمارك</span>
                        </a>
                    </div>
                </div>
            </div>

            {/* ── 2. Live Shipment Tracker & Fast Sourcing Widgets ── */}
            <div style={{ maxWidth: '1280px', margin: '-40px auto 60px auto', padding: '0 20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '25px' }}>

                    {/* Tracker Box */}
                    <div id="tracking-tool" className="luxvoo-card" style={{ borderTop: '4px solid #00f0ff' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                            <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'rgba(0, 240, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Truck size={24} color="#00f0ff" />
                            </div>
                            <div>
                                <h3 style={{ fontSize: '1.2rem', fontWeight: '800', margin: 0 }}>تتبع الشحنات والحاويات الحية</h3>
                                <p style={{ color: '#94a3b8', fontSize: '12px', margin: '2px 0 0 0' }}>أدخل رقم البوليصة، رقم الحاوية، أو كود التتبع</p>
                            </div>
                        </div>

                        <form onSubmit={handleTrackShipment} style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                            <input 
                                type="text"
                                className="luxvoo-input"
                                placeholder="مثال: LV-SA-91823 أو COSU918239..."
                                value={trackingNumber}
                                onChange={(e) => setTrackingNumber(e.target.value)}
                            />
                            <button type="submit" className="luxvoo-btn-gold" style={{ padding: '12px 20px', whiteSpace: 'nowrap' }} disabled={trackingLoading}>
                                {trackingLoading ? <RefreshCw size={18} className="animate-spin" /> : <Search size={18} />}
                                <span>تتبع</span>
                            </button>
                        </form>

                        {/* Tracker Telemetry Results */}
                        {trackingResult && (
                            <div style={{ marginTop: '20px', padding: '18px', background: 'rgba(0,0,0,0.5)', borderRadius: '16px', border: trackingResult.found ? '1px solid rgba(0, 240, 255, 0.4)' : '1px solid rgba(239, 68, 68, 0.4)' }}>
                                {trackingResult.found ? (
                                    <>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ fontSize: '15px', fontWeight: '800', color: '#00f0ff', fontFamily: 'monospace' }}>{trackingResult.trackingNo}</span>
                                                {trackingResult.trackingUrl && (
                                                    <a href={trackingResult.trackingUrl} target="_blank" rel="noreferrer" style={{ fontSize: '11px', color: '#d4af37', textDecoration: 'underline' }}>
                                                        (رابط الناقل الرسمي)
                                                    </a>
                                                )}
                                            </div>
                                            <span style={{ fontSize: '12px', background: 'rgba(16, 185, 129, 0.2)', color: '#10b981', padding: '4px 10px', borderRadius: '10px', fontWeight: '700' }}>
                                                {trackingResult.statusText}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#cbd5e1', marginBottom: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                            <div><strong>الناقل:</strong> {trackingResult.carrier}</div>
                                            <div><strong>خط السير:</strong> {trackingResult.origin} ➔ {trackingResult.destination}</div>
                                            <div><strong>المستلم:</strong> {trackingResult.recipientName || 'عميل لوكسفونو VIP'}</div>
                                            <div><strong>الوصول المتوقع:</strong> <span style={{ color: '#e5c07b' }}>{trackingResult.estimatedArrival}</span></div>
                                        </div>

                                        {/* Stepper */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            {(trackingResult.steps || []).map((st, i) => (
                                                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '12px' }}>
                                                    <div style={{ 
                                                        width: '22px', 
                                                        height: '22px', 
                                                        borderRadius: '50%', 
                                                        background: st.completed ? '#10b981' : (st.current ? '#d4af37' : '#334155'),
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                        marginTop: '2px'
                                                    }}>
                                                        {st.completed ? <CheckCircle2 size={13} color="#060913" /> : (st.current ? <Clock size={13} color="#060913" /> : null)}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: st.current ? '800' : '600', color: st.completed ? '#f8fafc' : (st.current ? '#e5c07b' : '#64748b') }}>
                                                            {st.title}
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{st.location} • {st.date}</div>
                                                        {st.description && <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{st.description}</div>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '12px', color: '#ef4444' }}>
                                        <AlertCircle size={24} style={{ margin: '0 auto 8px' }} />
                                        <div style={{ fontWeight: '700', fontSize: '14px' }}>{trackingResult.statusText}</div>
                                        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>{trackingResult.message}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Quick Features Highlight */}
                    <div className="luxvoo-card" style={{ borderTop: '4px solid #d4af37' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                            <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'rgba(212, 175, 55, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ShieldCheck size={24} color="#d4af37" />
                            </div>
                            <div>
                                <h3 style={{ fontSize: '1.2rem', fontWeight: '800', margin: 0 }}>مميزات وضمانات لوكسفونو للشحن (LuxVono Logistics)</h3>
                                <p style={{ color: '#94a3b8', fontSize: '12px', margin: '2px 0 0 0' }}>مستودعات مخصصة في شنتشن، جوانزو، ودبي</p>
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div className="service-pill">
                                <Ship size={20} color="#38bdf8" />
                                <div>
                                    <div style={{ fontWeight: '700', fontSize: '13px' }}>شحن بحري حاويات كاملة وجزئية (FCL / LCL)</div>
                                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>حجز مساحات مباشرة مع كبرى خطوط الملاحة البحرية العالمية</div>
                                </div>
                            </div>
                            <div className="service-pill">
                                <Plane size={20} color="#e5c07b" />
                                <div>
                                    <div style={{ fontWeight: '700', fontSize: '13px' }}>شحن جوي سريع واقتصادي (Express & Air Cargo)</div>
                                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>رحلات شحن يومية مباشرة من مطارات الصين والخليج</div>
                                </div>
                            </div>
                            <div className="service-pill">
                                <FileCheck size={20} color="#10b981" />
                                <div>
                                    <div style={{ fontWeight: '700', fontSize: '13px' }}>فحص الجودة الميداني وتوثيق الفيديو (QC Inspection)</div>
                                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>مهندسو جودة يفحصون البضائع بالمصنع قبل دفع الدفعة الأخيرة</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── 3. Freight Cost & Custom Tariff Calculator ── */}
            <div id="freight-calc" style={{ maxWidth: '1280px', margin: '0 auto 70px auto', padding: '0 20px' }}>
                <div className="luxvoo-card" style={{ background: 'linear-gradient(135deg, rgba(13, 19, 36, 0.9) 0%, rgba(6, 9, 19, 0.95) 100%)', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                        <div className="luxvoo-badge">
                            <Calculator size={16} />
                            <span>حاسبة أسعار الشحن والرسوم التقديرية</span>
                        </div>
                        <h2 style={{ fontSize: '1.8rem', fontWeight: '800', margin: '0 0 10px 0' }}>احسب تكلفة الشحن والاستيراد بدقة في ثوانٍ</h2>
                        <p style={{ color: '#94a3b8', fontSize: '14px' }}>حساب فوري لتكلفة الشحن الجوي والبحري والرسوم الجمركية بناءً على بيانات الشحنة</p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>نوع الشحن المفضل</label>
                            <select 
                                className="luxvoo-input"
                                value={calcData.freightType}
                                onChange={(e) => setCalcData({ ...calcData, freightType: e.target.value })}
                            >
                                <option value="AIR_EXPRESS">شحن جوي سريع (Express Air - 3-5 أيام)</option>
                                <option value="AIR_ECONOMY">شحن جوي اقتصادي (Economy Air - 7-10 أيام)</option>
                                <option value="SEA_LCL">شحن بحري جزئي (Sea LCL Consolidation)</option>
                                <option value="SEA_FCL_20">حاوية كاملة 20 قدم (20ft FCL Container)</option>
                                <option value="SEA_FCL_40">حاوية كاملة 40 قدم (40ft High Cube FCL)</option>
                            </select>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>بلد وميناء الانطلاق (Origin)</label>
                            <select 
                                className="luxvoo-input"
                                value={calcData.origin}
                                onChange={(e) => setCalcData({ ...calcData, origin: e.target.value })}
                            >
                                <option value="CN_SHENZHEN">الصين - مستودع شنتشن (Shenzhen Hub)</option>
                                <option value="CN_GUANGZHOU">الصين - مستودع جوانزو (Guangzhou Hub)</option>
                                <option value="CN_YIWU">الصين - مستودع إيوو (Yiwu Hub)</option>
                                <option value="CN_NINGBO">الصين - ميناء نينغبو (Ningbo Port)</option>
                                <option value="AE_DUBAI">الإمارات - مستودع دبي جبل علي (Dubai JAFZA)</option>
                                <option value="TR_ISTANBUL">تركيا - إسطنبول (Istanbul Port)</option>
                            </select>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>بلد وميناء الوصول (Destination)</label>
                            <select 
                                className="luxvoo-input"
                                value={calcData.destination}
                                onChange={(e) => setCalcData({ ...calcData, destination: e.target.value })}
                            >
                                <option value="SA_RIYADH">المملكة العربية السعودية - الرياض (RUH)</option>
                                <option value="SA_JEDDAH">المملكة العربية السعودية - ميناء جدة الإسلامي (JED)</option>
                                <option value="SA_DAMMAM">المملكة العربية السعودية - ميناء الملك عبد العزيز (DMM)</option>
                                <option value="AE_DUBAI">الإمارات العربية المتحدة - دبي (DXB)</option>
                                <option value="QA_DOHA">دولة قطر - الدوحة (DOH)</option>
                                <option value="KW_KUWAIT">دولة الكويت - ميناء الشويخ (KWI)</option>
                                <option value="OM_MUSCAT">سلطنة عُمان - مسقط (MCT)</option>
                                <option value="EG_CAIRO">جمهورية مصر العربية - القاهرة / الإسكندرية</option>
                            </select>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>الوزن الإجمالي (كجم)</label>
                            <input 
                                type="number" 
                                className="luxvoo-input"
                                value={calcData.weight}
                                onChange={(e) => setCalcData({ ...calcData, weight: e.target.value })}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>الحجم الإجمالي (متر مكعب CBM)</label>
                            <input 
                                type="number" 
                                step="0.01"
                                className="luxvoo-input"
                                value={calcData.cbm}
                                onChange={(e) => setCalcData({ ...calcData, cbm: e.target.value })}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>القيمة التقديرية للبضاعة ($ USD)</label>
                            <input 
                                type="number" 
                                className="luxvoo-input"
                                value={calcData.goodsValue}
                                onChange={(e) => setCalcData({ ...calcData, goodsValue: e.target.value })}
                            />
                        </div>
                    </div>

                    {/* Result Output Bar */}
                    {calcResult && (
                        <div style={{
                            background: 'rgba(6, 9, 19, 0.9)',
                            border: '1px solid rgba(212, 175, 55, 0.4)',
                            borderRadius: '20px',
                            padding: '25px',
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '20px',
                            alignItems: 'center',
                            textAlign: 'center'
                        }}>
                            <div>
                                <span style={{ fontSize: '12px', color: '#94a3b8', display: 'block' }}>تكلفة الشحن التقديرية</span>
                                <span style={{ fontSize: '1.5rem', fontWeight: '800', color: '#fff' }}>${calcResult.shippingCost}</span>
                            </div>
                            <div>
                                <span style={{ fontSize: '12px', color: '#94a3b8', display: 'block' }}>الرسوم الجمركية التقديرية (5%)</span>
                                <span style={{ fontSize: '1.2rem', fontWeight: '700', color: '#94a3b8' }}>${calcResult.customsEst}</span>
                            </div>
                            <div>
                                <span style={{ fontSize: '12px', color: '#94a3b8', display: 'block' }}>مدة الوصول المتوقعة</span>
                                <span style={{ fontSize: '1.1rem', fontWeight: '800', color: '#00f0ff' }}>{calcResult.deliveryDays}</span>
                            </div>
                            <div style={{ background: 'rgba(212, 175, 55, 0.1)', padding: '12px', borderRadius: '16px', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
                                <span style={{ fontSize: '12px', color: '#e5c07b', display: 'block', fontWeight: '700' }}>الإجمالي التقديري الشامل</span>
                                <span style={{ fontSize: '1.6rem', fontWeight: '900', color: '#d4af37' }}>${calcResult.totalEst}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── 4. Sourcing RFQ Submission Portal ── */}
            <div id="sourcing-rfq" style={{ maxWidth: '1280px', margin: '0 auto 80px auto', padding: '0 20px' }}>
                <div className="luxvoo-card">
                    <div style={{ textAlign: 'center', marginBottom: '35px' }}>
                        <div className="luxvoo-badge">
                            <FileText size={16} />
                            <span>طلب تسعير واستيراد مباشر من المصنع (RFQ)</span>
                        </div>
                        <h2 style={{ fontSize: '1.8rem', fontWeight: '800', margin: '0 0 10px 0' }}>أرسل مواصفات المنتج المطلوب وسنتفاوض مع المصانع بالنيابة عنك</h2>
                        <p style={{ color: '#94a3b8', fontSize: '14px', maxWidth: '750px', margin: '0 auto' }}>
                            فريقنا الميداني في شنتشن وجوانزو يتولى البحث عن المصنع المعتمد، طلب العينات، فحص الجودة الميداني، والتفاوض للحصول على أفضل سعر للجملة وتوريد الـ OEM/ODM.
                        </p>
                    </div>

                    {rfqSubmitted ? (
                        <div style={{ background: 'rgba(16, 185, 129, 0.15)', border: '1px solid #10b981', borderRadius: '20px', padding: '40px', textAlign: 'center' }}>
                            <CheckCircle2 size={48} color="#10b981" style={{ margin: '0 auto 15px auto' }} />
                            <h3 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#10b981', margin: '0 0 10px 0' }}>تم استلام طلب التوريد والاستيراد بنجاح!</h3>
                            <p style={{ color: '#cbd5e1', fontSize: '14px', maxWidth: '600px', margin: '0 auto 20px auto' }}>
                                تم إحالة ملف طلبك مباشرة إلى مكتب لوكسفو للتوريد في الصين. سيتواصل معك أحد مستشاري الاستيراد عبر الواتساب أو البريد خلال 24 ساعة بتقرير أولي وعروض أسعار المصانع.
                            </p>
                            <button onClick={() => setRfqSubmitted(false)} className="luxvoo-btn-gold">
                                <span>تقديم طلب توريد آخر</span>
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleRfqSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>قطاع / تصنيف المنتج *</label>
                                <select 
                                    className="luxvoo-input"
                                    value={rfqForm.productCategory}
                                    onChange={(e) => setRfqForm({ ...rfqForm, productCategory: e.target.value })}
                                >
                                    <option value="ELECTRONICS">إلكترونيات وأجهزة ذكية (Consumer Electronics)</option>
                                    <option value="INDUSTRIAL">معدات صناعية وخطوط إنتاج (Industrial Machinery)</option>
                                    <option value="APPAREL_TEXTILE">ملابس وأقمشة ومصنوعات جلدية (Apparel & Textiles)</option>
                                    <option value="HOME_BUILDING">أثاث ومواد بناء وديكور (Furniture & Building Materials)</option>
                                    <option value="LUXURY_GOODS">سلع فاخرة وقطع حصرية (Luxury Bespoke Sourcing)</option>
                                    <option value="GENERAL_GOODS">بضائع استهلاكية وتجارة عامة (General FMCG)</option>
                                </select>
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>اسم / نوع المنتج المطلوب بدقة *</label>
                                <input 
                                    type="text" 
                                    required
                                    className="luxvoo-input"
                                    placeholder="مثال: سماعات لاسلكية ANC بمواصفات خاصة OEM..."
                                    value={rfqForm.productName}
                                    onChange={(e) => setRfqForm({ ...rfqForm, productName: e.target.value })}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>الكمية المستهدفة (Units) *</label>
                                <input 
                                    type="number" 
                                    required
                                    className="luxvoo-input"
                                    placeholder="مثال: 500 قطعة"
                                    value={rfqForm.targetQuantity}
                                    onChange={(e) => setRfqForm({ ...rfqForm, targetQuantity: e.target.value })}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>الميزانية المستهدفة ($ USD - اختياري)</label>
                                <input 
                                    type="number" 
                                    className="luxvoo-input"
                                    placeholder="مثال: $5,000"
                                    value={rfqForm.targetBudget}
                                    onChange={(e) => setRfqForm({ ...rfqForm, targetBudget: e.target.value })}
                                />
                            </div>

                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>المواصفات الفنية ومتطلبات التغليف والشهادات</label>
                                <textarea 
                                    rows="4"
                                    className="luxvoo-input"
                                    placeholder="اذكر تفاصيل المنتج، المواد الخام، شهادات الجودة المطلوبة (CE / RoHS / SASO Saber)، ومتطلبات طباعة شعارك الخاص..."
                                    value={rfqForm.specifications}
                                    onChange={(e) => setRfqForm({ ...rfqForm, specifications: e.target.value })}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>الاسم الكامل أو اسم المؤسسة *</label>
                                <input 
                                    type="text" 
                                    required
                                    className="luxvoo-input"
                                    placeholder="الاسم الكريم"
                                    value={rfqForm.clientName}
                                    onChange={(e) => setRfqForm({ ...rfqForm, clientName: e.target.value })}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>رقم الجوال / الواتساب للتواصل المباشر *</label>
                                <input 
                                    type="tel" 
                                    required
                                    className="luxvoo-input"
                                    placeholder="+966 5X XXX XXXX"
                                    value={rfqForm.clientPhone}
                                    onChange={(e) => setRfqForm({ ...rfqForm, clientPhone: e.target.value })}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>البريد الإلكتروني</label>
                                <input 
                                    type="email" 
                                    className="luxvoo-input"
                                    placeholder="your-email@company.com"
                                    value={rfqForm.clientEmail}
                                    onChange={(e) => setRfqForm({ ...rfqForm, clientEmail: e.target.value })}
                                />
                            </div>

                            <div style={{ gridColumn: '1 / -1', textAlign: 'center', marginTop: '15px' }}>
                                <button type="submit" className="luxvoo-btn-gold" style={{ minWidth: '260px' }} disabled={rfqLoading}>
                                    {rfqLoading ? <RefreshCw size={18} className="animate-spin" /> : <Send size={18} />}
                                    <span>إرسال طلب الاستيراد والتفاوض</span>
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            {/* ── 5. Global Sourcing Hubs & Footer ── */}
            <div style={{ background: 'rgba(13, 19, 36, 0.9)', borderTop: '1px solid rgba(255, 255, 255, 0.08)', padding: '50px 5% 30px 5%' }}>
                <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '30px', marginBottom: '40px' }}>
                    <div>
                        <h4 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#d4af37', marginBottom: '15px' }}>LUXVONO GLOBAL LOGISTICS</h4>
                        <p style={{ color: '#94a3b8', fontSize: '13px', lineHeight: '1.6' }}>
                            الذراع المتخصص للشحن الدولي، الاستيراد المباشر من المصانع، وفحص الجودة التابع لمنظومة LUXVONO العالمية.
                        </p>
                    </div>

                    <div>
                        <h4 style={{ fontSize: '1rem', fontWeight: '700', color: '#fff', marginBottom: '15px' }}>مستودعاتنا ومكاتب التوريد</h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: '#94a3b8', fontSize: '13px', lineHeight: '2' }}>
                            <li>🇨🇳 الصين: شنتشن - منطقة باوان اللوجستية</li>
                            <li>🇨🇳 الصين: جوانزو - مركز التجارة الدولي</li>
                            <li>🇦🇪 الإمارات: دبي - المنطقة الحرة جبل علي</li>
                            <li>🇸🇦 السعودية: الرياض - المستودعات المركزية</li>
                        </ul>
                    </div>

                    <div>
                        <h4 style={{ fontSize: '1rem', fontWeight: '700', color: '#fff', marginBottom: '15px' }}>خدماتنا اللوجستية</h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: '#94a3b8', fontSize: '13px', lineHeight: '2' }}>
                            <li>• شحن بحري حاويات كاملة وجزئية (FCL / LCL)</li>
                            <li>• شحن جوي سريع بضائع تجارية</li>
                            <li>• فحص الجودة الميداني وتوثيق الفيديو (QC)</li>
                            <li>• التخليص الجمركي وإصدار شهادات سابر والمطابقة</li>
                        </ul>
                    </div>

                    <div>
                        <h4 style={{ fontSize: '1rem', fontWeight: '700', color: '#fff', marginBottom: '15px' }}>تواصل مباشر ومتابعة</h4>
                        <div style={{ color: '#94a3b8', fontSize: '13px', lineHeight: '1.8' }}>
                            <div>📧 البريد: sourcing@luxvono.com</div>
                            <div>📞 خدمة العملاء: +1 646 372 7645</div>
                            <div>⚡ استجابة فورية 24/7 عبر الواتساب والمحادثات</div>
                        </div>
                    </div>
                </div>

                <div style={{ textAlign: 'center', color: '#64748b', fontSize: '12px', borderTop: '1px solid rgba(255, 255, 255, 0.05)', paddingTop: '20px' }}>
                    © {new Date().getFullYear()} LUXVONO Global Sourcing & Shipping Division. All Rights Reserved. Powered by LuxVono Cloud Infrastructure.
                </div>
            </div>
        </div>
    );
};

export default LuxvooShippingHome;
